import React from "react";
import { ModernFolderCard } from "./ModernFolderCard";
import { ModernRiskCard } from "./ModernRiskCard";
import { ModernEmptyState } from "./ModernEmptyState";
import { AttachmentSectionHeader } from "./AttachmentSectionHeader";
import { SafetyRulesSection } from "./SafetyRulesSection";

import { Assessment } from "../../FolderContentsTypes";
import type { Database } from "@/integrations/supabase/types";


type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernContentGridProps {
  folderData: FolderType | null;
  subfolders: FolderType[];
  assessments: Assessment[];
  projectId: string;
  folderId: string;
  isShared: boolean;
  onOpenFolder: (folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onDeleteFolder: (folderId: string) => Promise<void>;
  onDeleteAssessment: (assessmentId: string) => Promise<void>;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  ensureFolderId?: () => Promise<string | null>;
  onRefresh?: () => void;
  /** Limit the listing to one role's documents */
  only?: "basp" | "basu";
  /** Project works without an arbetsmiljöplan */
  standalone?: boolean;

}

/** Documents that only make sense when the phase works towards an arbetsmiljöplan */
const isAmpOnlyDocument = (assessment: Assessment): boolean => {
  const phase = (assessment as any).phase ?? (assessment as any).data?.phase;
  return assessment.type === "risk_assessment" && phase === "bruksskede";
};

export const ModernContentGrid: React.FC<ModernContentGridProps> = ({
  folderData,
  subfolders,
  assessments,
  projectId,
  folderId,
  isShared,
  onOpenFolder,
  onNavigateToAssessment,
  onDeleteFolder,
  onDeleteAssessment,
  onCreateFolder,
  onStartAssessment,
  ensureFolderId,
  onRefresh,
  only,
  standalone = false
}) => {
  // Group assessments by type
  const showBasP = only !== "basu";
  const showBasU = only !== "basp";

  // I fristående läge visas inte dokument vars enda syfte är att mata arbetsmiljöplanen
  const visible = standalone ? assessments.filter(a => !isAmpOnlyDocument(a)) : assessments;

  const riskAssessments = showBasP ? visible.filter(a => a.type === 'risk_assessment') : [];
  const skrAssessments = showBasP ? visible.filter(a => a.type === 'skr_assessment') : [];
  const workPreparations = showBasU ? visible.filter(a => a.type === 'work_preparation') : [];

  const hasContent = (only ? false : subfolders.length > 0) ||
    riskAssessments.length > 0 || skrAssessments.length > 0 || workPreparations.length > 0;


  if (!hasContent && !only) {
    return (
      <div className="space-y-12">
        <ModernEmptyState
          folderData={folderData}
          projectId={projectId}
          folderId={folderId}
          isShared={isShared}
          onStartAssessment={onStartAssessment}
          onCreateFolder={onCreateFolder}
          ensureFolderId={ensureFolderId}
        />
      </div>
    );
  }


  const hasAttachments =
    skrAssessments.length > 0 || riskAssessments.length > 0 || workPreparations.length > 0;

  return (
    <div className="space-y-12">

      {!hasAttachments && only && (
        <p className="text-sm text-muted-foreground">
          Inga dokument skapade här ännu. Använd knapparna ovan för att komma igång.
        </p>
      )}

      {/* Dokumentlista */}
      {hasAttachments && (
        <div className="space-y-8">
          <div className="border-l-2 border-primary/40 pl-4">
            <h2 className="text-lg font-semibold">
              {standalone ? "Dokument i projektet" : "Bilagor till arbetsmiljöplanen"}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {standalone
                ? "Fristående dokument – varje dokument står för sig självt."
                : "Underlag som arbetsmiljöplanen hänvisar till."}
            </p>
          </div>

          {showBasP && <SafetyRulesSection projectId={projectId} />}

          {skrAssessments.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="SKR-bedömningar"
                count={skrAssessments.length}
                doc="03"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {skrAssessments.map(assessment => (
                  <div key={assessment.id} className="animate-fade-in">
                    <ModernRiskCard
                      assessment={assessment}
                      onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                      onDelete={() => onDeleteAssessment(assessment.id)}
                      projectId={projectId}
                      folderId={folderId}
                      onMoved={onRefresh}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {riskAssessments.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="Risksammanställningar"
                count={riskAssessments.length}
                doc="11"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {riskAssessments.map(assessment => (
                  <div key={assessment.id} className="animate-fade-in">
                    <ModernRiskCard
                      assessment={assessment}
                      onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                      onDelete={() => onDeleteAssessment(assessment.id)}
                      projectId={projectId}
                      folderId={folderId}
                      onMoved={onRefresh}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          {workPreparations.length > 0 && (
            <section className="space-y-4">
              <AttachmentSectionHeader
                title="Arbetsberedningar"
                count={workPreparations.length}
                description="Bas-U · utförandeskedet"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {workPreparations.map(assessment => (
                  <div key={assessment.id} className="animate-fade-in">
                    <ModernRiskCard
                      assessment={assessment}
                      onOpen={() => onNavigateToAssessment(assessment.assessment_id || assessment.id, folderId)}
                      onDelete={() => onDeleteAssessment(assessment.id)}
                      projectId={projectId}
                      folderId={folderId}
                      onMoved={onRefresh}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {!hasAttachments && showBasP && <SafetyRulesSection projectId={projectId} />}


      {/* Organisation – mappar */}
      {!only && subfolders.length > 0 && (
        <div className="space-y-4">
          <div className="border-l-2 border-border pl-4">
            <h2 className="text-lg font-semibold">Organisation</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Mappar används endast för att sortera dokumenten.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {subfolders.map(subfolder => (
              <div key={subfolder.id} className="animate-fade-in">
                <ModernFolderCard
                  folder={subfolder}
                  onOpen={() => onOpenFolder(subfolder.id)}
                  onDelete={() => onDeleteFolder(subfolder.id)}
                  assessmentCount={0}
                  subfolderCount={0}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

  );
};
